/**
 * Which keys NAME a secret, by the whole key (security-slice review rounds 6–7). A name that ends in a credential word
 * is the credential — whatever comes before it: `changePassword` is a real password field (tedious's Login7 payload;
 * round 7, Codex R7-1), as are `newPassword`, `confirmPassword`, `resetToken`. Only two explicit exemptions:
 *  - presence FLAGS about a credential, listed whole (`hasStoredPassword`, `badPassphrase`; round 6, Codex R6-3);
 *  - lexical / pagination / cancellation tokens (`cancellationToken`, `firstToken`, `nextPageToken`).
 * `isSecretKeyName` (`secretTextRedaction.ts`) asks this module first; tests in both directions live in
 * `secretTextRedaction.review2.test.ts`.
 */

/** A key's words: split on anything not a letter or digit and on camelCase boundaries, lower-cased. */
export function keyWords(name: string): string[] {
  return name.replace(/([a-z0-9])([A-Z])/gu, '$1 $2').toLowerCase().split(/[^a-z0-9]+/u).filter(Boolean);
}

/**
 * Presence flags and questions about a credential — whole keys, as words. Explicit on purpose: a prefix rule
 * (`has…`, `change…`) exposed `changePassword`. Add a flag here only with a test.
 */
const PRESENCE_FLAGS = new Set([
  'has password', 'had password', 'has stored password', 'has passphrase', 'has stored passphrase', 'has saved password',
  'is legacy password', 'is secret', 'bad password', 'bad passphrase', 'needs password', 'needs passphrase',
  'requires password', 'missing password', 'missing passphrase', 'invalid password', 'invalid token', 'use password',
  'show password', 'hide password', 'has valid signed token',
]);

/** Words before `token` that make it a lexical, pagination or cancellation token — not a credential (R6-3). */
const NON_CREDENTIAL_TOKEN = new Set([
  'cancellation', 'cancel', 'first', 'last', 'next', 'previous', 'prev', 'current', 'lexical', 'lexer', 'parser', 'sql',
  'keyword', 'identifier', 'continuation', 'page', 'sync', 'resume', 'cursor', 'start', 'end', 'eof',
]);

/** True when the key is a listed presence flag or a non-credential token — not the credential itself. */
export function namesSomethingElse(words: readonly string[]): boolean {
  if (PRESENCE_FLAGS.has(words.join(' '))) return true;
  return words.length > 1 && words[words.length - 1] === 'token' && words.slice(0, -1).some((word) => NON_CREDENTIAL_TOKEN.has(word));
}
